/** Central game state machine for Rush Fever 7s Deluxe */

import { Logger } from '../utils/Logger';

const logger = new Logger('GameStateMachine');

export enum GameState {
  IDLE = 'IDLE',
  SPINNING = 'SPINNING',
  EVALUATING = 'EVALUATING',
  PRESENTING_WINS = 'PRESENTING_WINS',
  FEATURE = 'FEATURE',
}

export type FeatureType = 'FREE_GAMES' | 'RUSH_FEVER' | 'JACKPOT_PICK';

export interface StateChangeEvent {
  from: GameState;
  to: GameState;
}

export type GameEventMap = {
  'state:change': StateChangeEvent;
  'spin:start': void;
  'reel:stopped': { reelIndex: number; symbols: string[] };
  'scatter:landed': { reelIndex: number; totalCount: number };
  'win:evaluated': unknown; // TODO: SpinOutcome type in Phase 2
  'win:presenting': unknown; // TODO: WinResult type in Phase 2
  'win:complete': { totalWin: number };
  'feature:start': { type: FeatureType };
  'feature:complete': { type: FeatureType; award: number };
  'balance:changed': { balance: number; lastWin: number };
};

type EventCallback<T> = (data: T) => void;

/** Lightweight typed event emitter for game events */
export class GameEventBus {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private listeners: Map<string, Set<EventCallback<any>>> = new Map();

  on<K extends keyof GameEventMap>(event: K, callback: EventCallback<GameEventMap[K]>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  off<K extends keyof GameEventMap>(event: K, callback: EventCallback<GameEventMap[K]>): void {
    this.listeners.get(event)?.delete(callback);
  }

  emit<K extends keyof GameEventMap>(event: K, data: GameEventMap[K]): void {
    this.listeners.get(event)?.forEach((cb) => cb(data));
  }
}

/** Valid state transitions */
const VALID_TRANSITIONS: Record<GameState, GameState[]> = {
  [GameState.IDLE]: [GameState.SPINNING],
  [GameState.SPINNING]: [GameState.EVALUATING],
  [GameState.EVALUATING]: [GameState.PRESENTING_WINS, GameState.FEATURE, GameState.IDLE],
  [GameState.PRESENTING_WINS]: [GameState.FEATURE, GameState.IDLE],
  [GameState.FEATURE]: [GameState.FEATURE, GameState.IDLE],
};

/** Central finite state machine driving the game loop */
export class GameStateMachine {
  private _state: GameState = GameState.IDLE;
  public readonly events: GameEventBus = new GameEventBus();

  get state(): GameState {
    return this._state;
  }

  /** Transition to a new state with validation */
  transition(to: GameState): boolean {
    if (!this.canTransition(to)) {
      logger.warn(`Invalid transition: ${this._state} -> ${to}`);
      return false;
    }

    const from = this._state;
    this._state = to;
    logger.info(`State: ${from} -> ${to}`);
    this.events.emit('state:change', { from, to });
    return true;
  }

  /** Check if a transition is valid */
  canTransition(to: GameState): boolean {
    return VALID_TRANSITIONS[this._state]?.includes(to) ?? false;
  }

  /** Check if the game is in IDLE state */
  get isIdle(): boolean {
    return this._state === GameState.IDLE;
  }

  /** Check if the game is spinning */
  get isSpinning(): boolean {
    return this._state === GameState.SPINNING;
  }
}
